import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Plus, Calendar, Utensils, BarChart3, Edit, Trash2, Clock } from 'lucide-react';
import { DynamicJournalForm } from '@/components/journal/DynamicJournalForm';
import { MealLogForm } from '@/components/journal/MealLogForm';
import { JournalCalendar } from '@/components/journal/JournalCalendar';
import {
    useDefaultDailyTemplate,
    useJournalTemplates,
    useMyEntriesByDate,
    useMyJournalStats,
    useMyDailyPointsSummary,
    useMyDailyMeals,
    useCreateJournalEntry,
    useUpdateJournalEntry,
    useCreateMealLog,
    useDeleteJournalEntry,
    useDeleteMealLog,
} from '@/hooks/useJournalApi';
import { FeatureGate } from '@/components/features/FeatureGate';
import { JournalLockedPage } from '@/components/features/FeatureLockedPage';
import { Feature } from '@/types/auth';
import {
    formatShortDay,
    fromLocalDateStr,
    isTodayLocalDateStr,
    todayLocalDateStr,
} from '@/lib/date';

const JournalContent = () => {
    const [selectedDate, setSelectedDate] = useState<string>(todayLocalDateStr());
    const [showJournalForm, setShowJournalForm] = useState(false);
    const [showMealForm, setShowMealForm] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const [editingEntry, setEditingEntry] = useState<any>(null);

    // Fetch data
    const { data: defaultTemplate, isLoading: defaultTemplateLoading } = useDefaultDailyTemplate();
    // When no template is marked default, fall back to any active daily one
    // rather than leaving Add Entry greyed out with no reason given (#106).
    // The list is ordered default first, then newest.
    const { data: activeDailyTemplates = [], isLoading: activeTemplatesLoading } =
        useJournalTemplates('DAILY', undefined, true);
    const journalTemplate = defaultTemplate || activeDailyTemplates[0] || null;
    const templateLoading = defaultTemplateLoading || (!defaultTemplate && activeTemplatesLoading);
    const { data: todayEntries = [], refetch: refetchEntries } = useMyEntriesByDate(selectedDate);
    const { data: journalStats } = useMyJournalStats();
    const { data: dailyPointsSummary } = useMyDailyPointsSummary(selectedDate);
    const { data: dailyMeals = [] } = useMyDailyMeals(selectedDate);

    // Mutations
    const createJournalEntry = useCreateJournalEntry();
    const updateJournalEntry = useUpdateJournalEntry();
    const createMealLog = useCreateMealLog();
    const deleteJournalEntry = useDeleteJournalEntry();
    const deleteMealLog = useDeleteMealLog();

    const formatFullDate = (dateString: string) => {
        const date = fromLocalDateStr(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const getTodayJournalEntry = () => {
        return todayEntries.find((entry) => entry.template?.type === 'DAILY');
    };

    const handleCreateJournalEntry = (data: any) => {
        if (editingEntry) {
            updateJournalEntry.mutate(
                { id: editingEntry.id, data },
                {
                    onSuccess: () => {
                        setShowJournalForm(false);
                        setEditingEntry(null);
                        refetchEntries();
                    },
                },
            );
        } else {
            createJournalEntry.mutate(data, {
                onSuccess: () => {
                    setShowJournalForm(false);
                    refetchEntries();
                },
            });
        }
    };

    const handleCreateMealLog = (data: any) => {
        createMealLog.mutate(data, {
            onSuccess: () => {
                setShowMealForm(false);
            },
        });
    };

    const handleDateSelect = (date: string) => {
        setSelectedDate(date);
        setShowCalendar(false);
    };

    const handleCreateEntryForDate = (date: string) => {
        setSelectedDate(date);
        setShowJournalForm(true);
    };

    const handleEditEntry = (entry: any) => {
        setEditingEntry(entry);
        setShowJournalForm(true);
    };

    if (templateLoading) {
        return (
            <div className="min-h-screen bg-brand-page flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 bg-brand-header rounded-full animate-pulse mx-auto mb-6"></div>
                    <p className="text-muted-foreground text-lg">Loading your journal...</p>
                </div>
            </div>
        );
    }

    // Show form overlays
    if (showJournalForm && journalTemplate) {
        return (
            <div className="min-h-screen bg-brand-page">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <DynamicJournalForm
                        template={journalTemplate}
                        date={selectedDate}
                        initialData={editingEntry?.responses}
                        onSubmit={handleCreateJournalEntry}
                        onCancel={() => {
                            setShowJournalForm(false);
                            setEditingEntry(null);
                        }}
                        isSubmitting={
                            editingEntry
                                ? updateJournalEntry.isPending
                                : createJournalEntry.isPending
                        }
                    />
                </div>
            </div>
        );
    }

    if (showMealForm) {
        return (
            <div className="min-h-screen bg-brand-page">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <MealLogForm
                        onSubmit={handleCreateMealLog}
                        onCancel={() => setShowMealForm(false)}
                        initialDate={selectedDate}
                        isSubmitting={createMealLog.isPending}
                    />
                </div>
            </div>
        );
    }

    if (showCalendar) {
        return (
            <div className="min-h-screen bg-brand-page">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <div className="mb-6">
                        <Button
                            variant="outline"
                            onClick={() => setShowCalendar(false)}
                            className="mb-4"
                        >
                            ← Back to Journal
                        </Button>
                    </div>
                    <JournalCalendar
                        selectedDate={selectedDate}
                        onDateSelect={handleDateSelect}
                        onCreateEntry={handleCreateEntryForDate}
                    />
                </div>
            </div>
        );
    }

    const todayEntry = getTodayJournalEntry();

    return (
        <div className="min-h-screen bg-brand-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12">
                {/* Header with Date Picker */}
                <div className="mb-6">
                    {/* Wraps rather than overflowing: on a phone the title, date
                        picker and the three actions do not fit on one line (#369). */}
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                            <h1 className="text-2xl font-bold text-foreground">Journal</h1>
                            <div className="flex items-center space-x-2">
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    className="px-3 py-1 text-sm border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent"
                                />
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setSelectedDate(todayLocalDateStr())}
                                    className="text-sm"
                                >
                                    Today
                                </Button>
                            </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-3">
                            {journalTemplate && (
                                <Button
                                    size="sm"
                                    className="rounded-lg"
                                    onClick={() => setShowJournalForm(true)}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Entry
                                </Button>
                            )}
                            <Button
                                size="sm"
                                variant="outline"
                                className="rounded-lg"
                                onClick={() => setShowMealForm(true)}
                            >
                                <Utensils className="w-4 h-4 mr-1" />
                                Meal
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                className="rounded-lg"
                                onClick={() => setShowCalendar(true)}
                            >
                                <Calendar className="w-4 h-4 mr-1" />
                                Calendar
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Content - Unified Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Main Content Area (3/4 width) */}
                    <div className="lg:col-span-3 space-y-6">
                        {/* Selected Day's Journal Entry */}
                        <div>
                            <h2 className="text-lg font-semibold text-foreground mb-4">
                                Journal Entry for {formatFullDate(selectedDate)}
                            </h2>
                            {todayEntry ? (
                                <Card className="p-6 bg-brand-header-wash border-border">
                                    <div className="flex items-start justify-between mb-4">
                                        <div>
                                            <h3 className="text-lg font-semibold text-foreground mb-1">
                                                {todayEntry.template?.name || 'Journal Entry'}
                                            </h3>
                                            <p className="text-sm text-muted-foreground">
                                                {todayEntry.pointsEarned} points •{' '}
                                                {todayEntry.isCompleted ? 'Complete' : 'Draft'}
                                            </p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleEditEntry(todayEntry)}
                                            >
                                                <Edit className="w-3 h-3 mr-1" />
                                                Edit
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    deleteJournalEntry.mutate(todayEntry.id)
                                                }
                                                disabled={deleteJournalEntry.isPending}
                                            >
                                                <Trash2 className="w-3 h-3" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="bg-card rounded-lg p-4">
                                        <p className="text-foreground">
                                            <strong>
                                                {Object.keys(todayEntry.responses).length}
                                            </strong>{' '}
                                            fields completed
                                        </p>
                                    </div>
                                </Card>
                            ) : (
                                <Card className="p-6 border-2 border-dashed border-border bg-muted">
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-muted rounded-xl flex items-center justify-center mx-auto mb-4">
                                            <BookOpen className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                        <h3 className="font-semibold text-foreground mb-2">
                                            No journal entry for this date
                                        </h3>
                                        {journalTemplate ? (
                                            <>
                                                <p className="text-sm text-muted-foreground mb-4">
                                                    Create your journal entry to get started
                                                </p>
                                                <Button onClick={() => setShowJournalForm(true)}>
                                                    <Plus className="w-4 h-4 mr-2" />
                                                    Create Entry
                                                </Button>
                                            </>
                                        ) : (
                                            <p className="text-sm text-muted-foreground">
                                                Journal entries are not set up yet. Your coach needs
                                                to add a daily journal template before you can write
                                                one. You can still log meals below.
                                            </p>
                                        )}
                                    </div>
                                </Card>
                            )}
                        </div>

                        {/* Meal Tracking Section */}
                        <div>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-foreground">
                                    Meals for {formatFullDate(selectedDate)}
                                </h2>
                                <Button size="sm" onClick={() => setShowMealForm(true)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Log Meal
                                </Button>
                            </div>

                            {dailyMeals.length === 0 ? (
                                <Card className="p-6 border-2 border-dashed border-border bg-muted">
                                    <div className="text-center">
                                        <div className="w-16 h-16 bg-muted rounded-xl flex items-center justify-center mx-auto mb-4">
                                            <Utensils className="w-8 h-8 text-muted-foreground" />
                                        </div>
                                        <h3 className="font-semibold text-foreground mb-2">
                                            No meals logged for this date
                                        </h3>
                                        <p className="text-sm text-muted-foreground mb-4">
                                            Start tracking your nutrition to earn Light Points
                                        </p>
                                        <Button onClick={() => setShowMealForm(true)}>
                                            <Plus className="w-4 h-4 mr-2" />
                                            Log Your First Meal
                                        </Button>
                                    </div>
                                </Card>
                            ) : (
                                <div className="space-y-3">
                                    {dailyMeals.map((meal) => (
                                        <Card
                                            key={meal.id}
                                            className="p-4 bg-card border border-border hover:shadow-md transition-shadow"
                                        >
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <div className="flex items-center space-x-3 mb-1">
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-brand-fill-lime text-brand-fill-ink text-xs"
                                                        >
                                                            {meal.mealType}
                                                        </Badge>
                                                        <span className="text-xs text-muted-foreground flex items-center">
                                                            <Clock className="w-3 h-3 mr-1" />
                                                            {new Date(
                                                                meal.timeEaten,
                                                            ).toLocaleTimeString('en-US', {
                                                                hour: 'numeric',
                                                                minute: '2-digit',
                                                            })}
                                                        </span>
                                                    </div>
                                                    <h4 className="font-medium text-foreground mb-1">
                                                        {meal.mealSource === 'RECIPE'
                                                            ? meal.recipe?.name
                                                            : meal.customMealName}
                                                    </h4>
                                                    <p className="text-sm text-brand-text-green font-medium">
                                                        {meal.totalPoints} Light Points
                                                    </p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => deleteMealLog.mutate(meal.id)}
                                                    disabled={deleteMealLog.isPending}
                                                    className="text-destructive hover:bg-destructive/10"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar (1/4 width) */}
                    <div className="space-y-6">
                        {/* Compact Nutrition Summary */}
                        {dailyPointsSummary && (
                            <Card className="p-4 bg-brand-header text-brand-header-ink">
                                <h3 className="font-semibold mb-3 flex items-center text-sm">
                                    <Utensils className="w-4 h-4 mr-2" />
                                    {isTodayLocalDateStr(selectedDate)
                                        ? 'Nutrition Today'
                                        : `Nutrition for ${formatShortDay(selectedDate)}`}
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-xs opacity-90">Meals</span>
                                        <span className="font-semibold">
                                            {dailyPointsSummary.totalMeals}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-xs opacity-90">Points</span>
                                        <span className="font-semibold">
                                            {dailyPointsSummary.totalPoints}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-xs opacity-90">Goals Met</span>
                                        <span className="font-semibold">
                                            {
                                                dailyPointsSummary.categoryTotals.filter(
                                                    (c) => c.isGoalMet,
                                                ).length
                                            }
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* Compact Journal Analytics */}
                        {journalStats && (
                            <Card className="p-4 bg-brand-header text-brand-header-ink">
                                <h3 className="font-semibold mb-3 flex items-center text-sm">
                                    <BarChart3 className="w-4 h-4 mr-2" />
                                    Journal Stats
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-xs opacity-90">Total Entries</span>
                                        <span className="font-semibold">
                                            {journalStats.totalEntries}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        {/* Days in a row with a completed entry. Named
                                            apart from the Meals page's Current Streak,
                                            which counts days with a meal logged. */}
                                        <span className="text-xs opacity-90">Journal Streak</span>
                                        <span className="font-semibold">
                                            {journalStats.currentStreak}{' '}
                                            {journalStats.currentStreak === 1 ? 'day' : 'days'}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        {/* Every point scored, from logged meals, not
                                            only on days with an entry (#371). */}
                                        <span className="text-xs opacity-90">Total Points</span>
                                        <span className="font-semibold">
                                            {journalStats.totalPoints}
                                        </span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-xs opacity-90">Completion</span>
                                        <span className="font-semibold">
                                            {Math.round(journalStats.completionRate)}%
                                        </span>
                                    </div>
                                </div>
                            </Card>
                        )}

                        {/* Quick Category Breakdown */}
                        {dailyPointsSummary && dailyPointsSummary.categoryTotals.length > 0 && (
                            <Card className="p-4">
                                <h3 className="font-semibold mb-3 text-sm text-foreground">
                                    Categories{' '}
                                    <span className="font-normal text-muted-foreground">
                                        (servings)
                                    </span>
                                </h3>
                                <div className="space-y-2">
                                    {dailyPointsSummary.categoryTotals
                                        .slice(0, 5)
                                        .map((category) => (
                                            <div
                                                key={category.categoryId}
                                                className="flex items-center justify-between"
                                            >
                                                <span className="text-xs text-muted-foreground truncate flex-1 mr-2">
                                                    {category.categoryName}
                                                </span>
                                                <div className="flex items-center space-x-1">
                                                    <div
                                                        className={`w-2 h-2 rounded-full ${
                                                            category.isGoalMet
                                                                ? 'bg-brand-fill-green'
                                                                : 'bg-muted'
                                                        }`}
                                                    ></div>
                                                    <span className="text-xs font-medium">
                                                        {category.servings}
                                                        {category.target && `/${category.target}`}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const Journal = () => {
    return (
        <FeatureGate feature={Feature.JOURNAL} fallback={<JournalLockedPage />}>
            <JournalContent />
        </FeatureGate>
    );
};

export default Journal;
